/**
 * DismissibleHint — banner/explainer che l'utente può chiudere con X.
 * La scelta viene persistita in localStorage (key: `pronto:hint:{id}:dismissed`)
 * così non riappare alle visite successive.
 *
 * Uso:
 *   <DismissibleHint id="condivisione-intro">
 *     <h2>Spiegazione...</h2>
 *   </DismissibleHint>
 */
import React, { useState, useEffect } from "react";
import { X } from "@phosphor-icons/react";

const KEY_PREFIX = "pronto:hint:";

export function DismissibleHint({ id, children, className = "", testId }) {
  const storageKey = `${KEY_PREFIX}${id}:dismissed`;
  const [dismissed, setDismissed] = useState(true); // default hidden until effect

  useEffect(() => {
    try {
      const v = localStorage.getItem(storageKey);
      setDismissed(v === "1");
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  // iter v28 r177 — listener per "Ripristina suggerimenti" (Profilo → Account):
  // quando l'utente chiede di rivederli tutti, ri-mostriamo senza reload.
  // Ascolto entrambi gli eventi ("pronto:hints-reset" e "pronto:intros-reset")
  // così il bottone unico nel profilo riapre sia DismissibleHint sia
  // DismissibleIntro contemporaneamente.
  useEffect(() => {
    const onRestore = () => setDismissed(false);
    window.addEventListener("pronto:hints-reset", onRestore);
    window.addEventListener("pronto:intros-reset", onRestore);
    return () => {
      window.removeEventListener("pronto:hints-reset", onRestore);
      window.removeEventListener("pronto:intros-reset", onRestore);
    };
  }, []);

  if (dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, "1"); } catch { /* quota full */ }
    setDismissed(true);
  };

  return (
    <div
      className={`relative ${className}`}
      data-testid={testId || `hint-${id}`}
    >
      {children}
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/70 backdrop-blur-sm text-[#6B6A65] hover:text-[#1C1C1A] hover:bg-[#EDEAE2] transition-colors z-10"
        aria-label="Nascondi questa spiegazione"
        title="Ho capito · non mostrarla più"
        data-testid={`hint-${id}-close`}
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}

/**
 * Ripristina tutti i suggerimenti dismessi (chiamato da Profilo → Account).
 * Cancella tutte le chiavi `pronto:hint:*:dismissed` e notifica i componenti
 * montati via custom event "pronto:hints-reset" così tornano visibili
 * senza reload.
 */
export function resetAllHints() {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch { /* no-op */ }
  window.dispatchEvent(new CustomEvent("pronto:hints-reset"));
}

export default DismissibleHint;
